import { useEffect, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
// eslint-disable-next-line camelcase
import { post_api } from '../../../redux/api_funcs';
import { getCookie } from '../../../util_functions';
// eslint-disable-next-line camelcase
import { get_api_url } from '../../../utils/urls';
import {
  currentFingerprint,
  getLatestFingerprint,
  requestFingerprintCheck
} from '../../util_components/bundle_fingerprint';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

const DEVICE_EVENT_ENDPOINT = '/v2/twilio_call/device_event';

const TAB_ID_STORAGE_KEY = 'twilio_agent_tab_id';

// The backend accepts up to 50 events per request; flush well under that so a
// burst (a heal ladder firing) still lands in one post.
const FLUSH_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;
// Hard ceiling the backend enforces. A queue can exceed FLUSH_BATCH_SIZE (a
// pagehide flush drains whatever is there), and an oversized request is
// rejected whole, taking the device_error rows with it.
const MAX_EVENTS_PER_POST = 50;

// A tab stuck in a rebuild loop must not be able to turn telemetry into a load
// source of its own.
const RATE_LIMIT_MAX_EVENTS = 120;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;

const TAB_CHANNEL_NAME = 'hridaya-twilio-agent-tabs';
const TAB_HEARTBEAT_MS = 15000;
const TAB_SWEEP_MS = 5000;
// Two missed heartbeats plus slack: a backgrounded tab's timers are throttled,
// and evicting it too early is what would make the count flap.
const TAB_STALE_MS = 40000;
const TAB_COUNT_EVENT_DEBOUNCE_MS = 2000;

let tabId = null;
let tabIdPersisted = false;
let queue = [];
let flushTimer = null;
let windowStartedAt = 0;
let windowCount = 0;
// Latched on a 401 with the token that was refused: the ops session is over, so nothing is queued or posted
// until a different cms_token_3 is in place (a re-login in the same tab).
let expiredToken = null;
const sessionIsExpired = () => {
  if (expiredToken === null) return false;
  const token = getCookie('cms_token_3');
  return !token || token === expiredToken;
};

const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // falls through to the Math.random id below
  }
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Stable id for this browser tab; survives reload, differs per tab. */
export const getTabId = () => {
  if (tabId) return tabId;
  try {
    const stored = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (stored) {
      tabId = stored;
      tabIdPersisted = true;
      return tabId;
    }
    tabId = randomId();
    window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
    tabIdPersisted = true;
    return tabId;
  } catch (e) {
    // Private mode / blocked storage: an in-memory id still groups the events
    // of one page lifetime, it just does not survive a reload.
    tabId = randomId();
    return tabId;
  }
};

/**
 * Copy for the multi-tab case. Every tab still registers its own Device (that
 * redundancy is the safety net), but the tabs now elect one answer tab, so
 * this is an indicator rather than a warning — see voice_coordinator.js.
 */
export const multiTabWarning = (count) =>
  `You have ${count} MyYogaTeacher CMS tabs open. Calls will ring in the tab you used most recently; the other tabs stay quiet and take over only if that tab stops responding.`;

// --- tab presence -----------------------------------------------------------

// peer tab_id -> { at, build }: when we last heard from it and its bundle.
const peers = new Map();
const countSubscribers = new Set();
const checkedBuilds = new Set();
let channel = null;
let heartbeatTimer = null;
let sweepTimer = null;
let presenceStarted = false;
let presenceUsers = 0;
let openTabs = 1;
let tabCountEventTimer = null;

export const getOpenTabsCount = () => openTabs;

/** True when a live peer tab announced it is running bundle `build`. */
export const hasPeerOnBuild = (build) => {
  if (!build) return false;
  const cutoff = Date.now() - TAB_STALE_MS;
  let found = false;
  peers.forEach((peer) => {
    if (!found && peer.build === build && peer.at >= cutoff) found = true;
  });
  return found;
};

const navigationType = () => {
  try {
    return performance.getEntriesByType('navigation')[0]?.type || null;
  } catch (e) {
    return null;
  }
};

// Carried in `data`: the backend's snapshot struct drops unknown keys.
const pageLoadData = () => {
  getTabId();
  return {
    nav_type: navigationType(),
    session_storage_ok: tabIdPersisted,
    build: currentFingerprint || null
  };
};

const snapshotOf = (event) => {
  if (event !== 'page_load' && event !== 'device_registered') return undefined;
  let sdkVersion = null;
  try {
    sdkVersion = Device.version || null;
  } catch (e) {
    sdkVersion = null;
  }
  return {
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    sdk_version: sdkVersion
  };
};

const postChunk = (events) => {
  const body = events.length === 1 ? events[0] : { events };
  try {
    const sent = post_api(getUrl(DEVICE_EVENT_ENDPOINT), body, true);
    if (sent && typeof sent.catch === 'function') {
      sent.catch((err) => {
        // Telemetry is best-effort; a failed post must never surface to the agent.
        if (err?.response?.status === 401) {
          expiredToken = getCookie('cms_token_3') || '';
          queue = [];
        }
      });
    }
  } catch (e) {
    // swallowed for the same reason
  }
};

const postEvents = (events) => {
  for (let i = 0; i < events.length; i += MAX_EVENTS_PER_POST) {
    postChunk(events.slice(i, i + MAX_EVENTS_PER_POST));
  }
};

const flushDeviceEvents = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (sessionIsExpired()) {
    queue = [];
    return;
  }
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  postEvents(batch);
};

const withinRateLimit = () => {
  const now = Date.now();
  if (now - windowStartedAt > RATE_LIMIT_WINDOW_MS) {
    windowStartedAt = now;
    windowCount = 0;
  }
  if (windowCount >= RATE_LIMIT_MAX_EVENTS) return false;
  windowCount += 1;
  return true;
};

// The backend decodes the batch into a typed struct and rejects the WHOLE
// request when one field has the wrong JSON type — and Twilio error codes are
// numbers (31005) against a string field, so the register_failed and
// device_error rows we most need were exactly the ones being dropped. Coerce
// the typed fields here rather than at each call site.
const STRING_FIELDS = ['code', 'reason', 'device_state', 'visibility'];
const INT_FIELDS = ['open_tabs', 'attempt'];

const normalisePayload = (fields) => {
  const payload = {};
  Object.keys(fields).forEach((key) => {
    const value = fields[key];
    const typed = STRING_FIELDS.includes(key) || INT_FIELDS.includes(key);
    // A null is not a string or an int; omitting the key says the same thing
    // and still decodes.
    if (typed && (value === null || value === undefined)) return;
    if (STRING_FIELDS.includes(key)) {
      payload[key] = String(value);
      return;
    }
    if (INT_FIELDS.includes(key)) {
      const int = Math.trunc(Number(value));
      if (Number.isFinite(int)) payload[key] = int;
      return;
    }
    payload[key] = value;
  });
  return payload;
};

/**
 * Queue one device-lifecycle event. Never throws; drops rather than retries.
 * `fields` carries the event-specific keys (reason, code, attempt, data, ...).
 */
export const reportDeviceEvent = (event, fields = {}) => {
  try {
    if (sessionIsExpired()) return;
    if (!withinRateLimit()) return;
    const payload = normalisePayload({
      tab_id: getTabId(),
      event,
      visibility: typeof document !== 'undefined' ? document.visibilityState : null,
      open_tabs: openTabs,
      at: new Date().toISOString(),
      ...fields,
      ...(event === 'page_load' ? { data: { ...pageLoadData(), ...fields.data } } : {})
    });
    const snapshot = snapshotOf(event);
    if (snapshot) payload.snapshot = snapshot;

    queue.push(payload);
    if (queue.length >= FLUSH_BATCH_SIZE) {
      flushDeviceEvents();
      return;
    }
    if (!flushTimer) flushTimer = setTimeout(flushDeviceEvents, FLUSH_INTERVAL_MS);
  } catch (e) {
    // Diagnostics must never break the call path.
  }
};

const setOpenTabs = (next) => {
  if (next === openTabs) return;
  openTabs = next;
  countSubscribers.forEach((fn) => {
    try {
      fn(next);
    } catch (e) {
      // a broken subscriber must not stop the others
    }
  });
  // Debounced: a browser restoring a session opens every tab at once, which
  // would otherwise emit one event per peer discovered.
  if (tabCountEventTimer) clearTimeout(tabCountEventTimer);
  tabCountEventTimer = setTimeout(() => {
    tabCountEventTimer = null;
    reportDeviceEvent('tab_count', { data: { open_tabs: openTabs } });
  }, TAB_COUNT_EVENT_DEBOUNCE_MS);
};

const recomputeCount = () => setOpenTabs(peers.size + 1);

const announce = (kind) => {
  try {
    channel?.postMessage({ kind, tab_id: getTabId(), at: Date.now(), build: currentFingerprint });
  } catch (e) {
    // channel closed underneath us
  }
};

const onPeerMessage = (message) => {
  const data = message?.data;
  if (!data || !data.tab_id || data.tab_id === getTabId()) return;
  if (data.kind === 'bye') {
    if (peers.delete(data.tab_id)) recomputeCount();
    return;
  }
  peers.set(data.tab_id, { at: Date.now(), build: data.build || null });
  // A peer on a build this tab has not seen may be the next deploy. Each
  // unfamiliar build is checked once, so an older peer cannot keep polling.
  const { build } = data;
  if (
    build &&
    build !== currentFingerprint &&
    build !== getLatestFingerprint() &&
    !checkedBuilds.has(build) &&
    requestFingerprintCheck()
  ) {
    checkedBuilds.add(build);
  }
  recomputeCount();
  // A newcomer does not know about us yet, so answer its roll call.
  if (data.kind === 'hello' || data.kind === 'who') announce('heartbeat');
};

const sweepPeers = () => {
  const cutoff = Date.now() - TAB_STALE_MS;
  let changed = false;
  peers.forEach((peer, id) => {
    if (peer.at < cutoff) {
      peers.delete(id);
      changed = true;
    }
  });
  if (changed) recomputeCount();
};

/**
 * Start the cross-tab presence protocol. Idempotent; safe to call from every
 * component that wants the count. Returns a release function; the protocol
 * only stops once every holder has released it.
 */
export const startTabPresence = () => {
  presenceUsers += 1;
  // Ref-counted: the page-level hook and the call manager both start it, and
  // whichever unmounts first must not take presence away from the other.
  const release = (stop) => () => {
    presenceUsers -= 1;
    if (presenceUsers <= 0) stop();
  };
  if (presenceStarted) return release(() => {});
  presenceStarted = true;

  if (typeof BroadcastChannel === 'undefined') {
    // Without the API we cannot see peers at all; claiming 1 is the only
    // honest answer, and it keeps the warning off rather than wrong.
    setOpenTabs(1);
    return release(() => {});
  }

  try {
    channel = new BroadcastChannel(TAB_CHANNEL_NAME);
  } catch (e) {
    channel = null;
    setOpenTabs(1);
    return release(() => {});
  }

  channel.onmessage = onPeerMessage;
  announce('hello');
  announce('who');
  heartbeatTimer = setInterval(() => announce('heartbeat'), TAB_HEARTBEAT_MS);
  sweepTimer = setInterval(sweepPeers, TAB_SWEEP_MS);

  const onPageHide = () => {
    announce('bye');
    flushDeviceEvents();
  };
  window.addEventListener('pagehide', onPageHide);

  return release(() => {
    window.removeEventListener('pagehide', onPageHide);
    clearInterval(heartbeatTimer);
    clearInterval(sweepTimer);
    announce('bye');
    try {
      channel?.close();
    } catch (e) {
      // already closed
    }
    channel = null;
    peers.clear();
    presenceStarted = false;
  });
};

/** Live count of CMS tabs this browser has open, including this one. */
export const useOpenTabsCount = () => {
  const [count, setCount] = useState(openTabs);
  useEffect(() => {
    const release = startTabPresence();
    setCount(getOpenTabsCount());
    countSubscribers.add(setCount);
    return () => {
      countSubscribers.delete(setCount);
      release();
    };
  }, []);
  return count;
};

export { flushDeviceEvents };
